import { FC, SVGProps } from "react";

import { IconName } from "./IconName";
import { IconSize } from "./IconSize";
import { availableIcons } from "./iconLoader";

/**
 * Gets the icon for the specified name and size.
 * @param name The name of the icon to retrieve.
 * @param size The size of the icon to retrieve.
 * @returns The icon component or null if none found.
 */
export function getIconComponent(name: IconName, size: IconSize): FC<SVGProps<SVGSVGElement> & { title: string }> | null {
  const fallBackMap: Record<IconSize, IconSize[]> = {
    xsmall: ["small", "regular"],
    small: ["regular"],
    regular: ["small"],
    large: ["regular", "small"],
  };

  const triedSizes: Set<IconSize> = new Set();

  const tryGetIcon = (s: IconSize): FC<SVGProps<SVGSVGElement> & { title: string }> | null => {
    if (triedSizes.has(s)) return null;
    triedSizes.add(s);
    try {
      const module = availableIcons(`./${s}/${name}.svgi`);
      if (module?.default) return module.default;
    } catch {
      return null;
    }
    return null;
  };
  let icon = tryGetIcon(size);
  if (icon) return icon;

  const fallbacks = fallBackMap[size] ?? [];
  for (const fallBackSize of fallbacks) {
    icon = tryGetIcon(fallBackSize);
    if (icon) return icon;
  }

  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production" && process.env?.NODE_ENV !== "test") {
    console.warn(`[capitalrx-components] Icon "${name}" not found at size "${size}" or fallbacks [${fallbacks.join(", ")}].`);
  }

  return null;
}
